import { useEffect } from 'react'
import type maplibregl from 'maplibre-gl'
import * as turf from '@turf/turf'

const SOURCE_ID = 'field-accuracy'
const FILL_ID = 'field-accuracy-fill'
const STROKE_ID = 'field-accuracy-stroke'
const CENTRE_SOURCE_ID = 'field-accuracy-centre'
const CENTRE_ID = 'field-accuracy-centre-dot'

export interface AccuracyCircleProps {
	map: maplibregl.Map | null
	latitude: number | null
	longitude: number | null
	/** Radius in metres from the device. Nothing is drawn without it. */
	accuracy: number | null
	/**
	 * Also draw a dot at the centre — "you are here".
	 *
	 * Off by default because on the capture screen a draggable pin already
	 * marks the spot and a second dot under it just reads as a rendering bug.
	 * On a map of other people's places it is the whole point of orientation.
	 */
	showCentre?: boolean
}

/**
 * Draws how sure the device is about where it is.
 *
 * This is honesty, not decoration. GPS accuracy is commonly 20-50m inside a
 * centre or between big-box buildings, and a bare pin implies a precision the
 * device does not have — which is how somebody ends up touring the unit next
 * door. Showing the radius lets the user decide whether to trust the fix or
 * drag the pin to where they actually are.
 *
 * A real metres radius, not a pixel one: a `circle` layer would keep the same
 * screen size at every zoom, which would mean it stopped describing anything.
 * turf builds the polygon, the same approach `TradeAreaRings` uses.
 */
export function AccuracyCircle({
	map,
	latitude,
	longitude,
	accuracy,
	showCentre = false,
}: AccuracyCircleProps) {
	useEffect(() => {
		if (!map || latitude == null || longitude == null || !showCentre) return

		const point = {
			type: 'Feature' as const,
			geometry: { type: 'Point' as const, coordinates: [longitude, latitude] },
			properties: {},
		}

		const existing = map.getSource(CENTRE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
		if (existing) {
			existing.setData(point as never)
			return
		}

		map.addSource(CENTRE_SOURCE_ID, { type: 'geojson', data: point as never })
		map.addLayer({
			id: CENTRE_ID,
			type: 'circle',
			source: CENTRE_SOURCE_ID,
			paint: {
				// A fixed pixel size, unlike the accuracy ring: this one marks a
				// point, not an area, so it should stay legible at every zoom.
				'circle-radius': 6,
				'circle-color': '#0080fe',
				'circle-stroke-width': 2,
				'circle-stroke-color': '#ffffff',
			},
		})

		return () => {
			try {
				if (map.getLayer(CENTRE_ID)) map.removeLayer(CENTRE_ID)
				if (map.getSource(CENTRE_SOURCE_ID)) map.removeSource(CENTRE_SOURCE_ID)
			} catch {
				/* Map already destroyed. */
			}
		}
	}, [map, latitude, longitude, showCentre])

	useEffect(() => {
		if (!map || latitude == null || longitude == null || !accuracy) return

		const polygon = turf.circle([longitude, latitude], accuracy, {
			steps: 64,
			units: 'meters',
		})

		const existing = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined
		if (existing) {
			existing.setData(polygon as never)
			return
		}

		map.addSource(SOURCE_ID, { type: 'geojson', data: polygon as never })
		map.addLayer({
			id: FILL_ID,
			type: 'fill',
			source: SOURCE_ID,
			paint: { 'fill-color': '#0080fe', 'fill-opacity': 0.12 },
		})
		map.addLayer({
			id: STROKE_ID,
			type: 'line',
			source: SOURCE_ID,
			paint: { 'line-color': '#0080fe', 'line-opacity': 0.4, 'line-width': 1 },
		})

		return () => {
			// The map may already be gone: React unmounts children before parents,
			// so MapContainer can have called map.remove() before this runs.
			try {
				if (map.getLayer(STROKE_ID)) map.removeLayer(STROKE_ID)
				if (map.getLayer(FILL_ID)) map.removeLayer(FILL_ID)
				if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
			} catch {
				/* Map already destroyed. */
			}
		}
	}, [map, latitude, longitude, accuracy])

	return null
}

export default AccuracyCircle
